import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocaleNavigate, LocaleLink } from '../i18n/locale-link';
import { useLocale } from '../i18n/locale-context';
import { useAuth } from '../lib/auth-context';
import { useAuthOrigin } from '../lib/auth-origin';
import { savedSearchCapFor } from '../lib/saved-search-cap';
import { createSavedSearch, listSavedSearches, type SavedSearch } from '../lib/saved-search-client';
import { SAVED_SEARCH_TERMS_MAX } from '../lib/saved-search-limits';
import { stashPendingSavedSearch } from '../lib/pending-saved-search';
import { requestOnboardingSearch } from '../lib/onboarding-request';
import { trackSavedSearchActivation } from '../lib/tracking';
import { fetchRegions, fetchCategories, fetchCatalog } from '../lib/catalog-client';
import { ApiError } from '../lib/api';
import { SavedSearchEditSheet } from './saved-search-edit-sheet';
import { AlertPicker } from './alert-picker';
import { TagInput } from './ui/tag-input';
import { BudgetInput, BUDGET_FLOOR_PICKS } from './ui/budget-input';
import { BookmarkIcon, CheckIcon } from './icons';
import { uuid } from '../lib/uuid';
import { countryConfig } from '../country';

interface SaveSearchButtonProps {
  /** Active catalog filters to persist. */
  q?: string;
  region?: string;
  category?: string;
  /** ИКПУ niche code — the axis that drives both alerts and free-tier contacts. */
  ikpu?: string;
  /** Curated Niche (Spec #3) slug — the same single axis as `category`/`ikpu`;
   *  exactly one of the three is ever set. */
  nicheSlug?: string;
  /** Fixed catalog layer the page was browsed in (e.g. 'ifi' on /ifi).
   *  MUST be threaded through to the saved search, or a subscriber saving
   *  from /ifi gets a `gov`-scoped search and silently never hears about IFI
   *  tenders again. Undefined on the plain catalog is a no-op — the API's own
   *  `SavedSearch.scope` column default (`gov`) applies, same as before. */
  scope?: string;
  /** Make the button prominent (solid brand) — the activation nudge's step 2. */
  highlight?: boolean;
  /** Show the button but disabled (no filter selected yet) instead of hiding it. */
  disabled?: boolean;
  /**
   * How many open tenders the CURRENT filters match, straight from the list
   * the user is already looking at (`data.total`) — no extra request.
   *
   * Measured on prod 2026-08-02: **152 of 589 saved searches older than 48h
   * (26%) have never fired once.** Those filters are too narrow to ever match,
   * and nothing tells the user — they just wait forever for an alert that
   * cannot arrive. Showing the count at creation time turns a silent dead end
   * into a visible one, which is cheaper than any catch-up digest and fixes
   * the cause rather than the symptom.
   */
  matchCount?: number;
  /** Replaces «Сохранить поиск» on the create button — «Следить за такими»
   *  under a closed lot's «Похожие открытые лоты». Same flow, other words; the
   *  «Настроить оповещения» state keeps its own label, because there it edits
   *  an alert the reader already has. */
  label?: string;
  /** Name suggestion when the filters alone cannot name the search (a bare
   *  ИКПУ code has no display name here). Falls back to «Мой поиск». */
  defaultName?: string;
  /**
   * Spain's first screen (`features.alertLanding`): pressed with NO criterion,
   * the button asks for the onboarding search step («¿Qué ofreces?») instead
   * of saving — a guest goes to /register first and gets the step once signed
   * in, a signed-in reader gets it at once (`lib/onboarding-request.ts`).
   * Nothing is stashed: an empty search is refused by the API. Absent = the
   * button saves what is picked, exactly as before.
   */
  emptyOpensOnboarding?: boolean;
  /**
   * The catalogue's budget floor / ceiling, when set (so'm; EUR on Spain). Only a
   * GUEST's stash carries them, and only where `features.guestSearchCarried` is
   * on (Spain, persona test 22.10, D1): the search he registers for is the list
   * he was looking at, «Presupuesto desde 100000» included. A signed-in reader
   * sets his floor in the popover as before. Absent = nothing added to the stash.
   */
  minBudget?: number;
  maxBudget?: number;
}

/**
 * "Сохранить поиск" pill shown in the catalog result row for signed-in users.
 * Opens an inline popover with a prefilled name input and Save/Cancel.
 * On success it invalidates the shared ['saved-searches'] cache so the sidebar
 * and dashboard refresh, then shows a brief "Сохранено ✓" confirmation.
 */
export function SaveSearchButton({
  q,
  region,
  category,
  ikpu,
  nicheSlug,
  scope,
  highlight = false,
  disabled = false,
  matchCount,
  label,
  defaultName,
  emptyOpensOnboarding = false,
  minBudget: catalogMinBudget,
  maxBudget: catalogMaxBudget,
}: SaveSearchButtonProps) {
  const { t, locale } = useLocale();
  // `all` is the catalog's browse-only pseudo-scope — a text search from the
  // home page spans every layer, so `/catalog?q=…` resolves to it. A saved
  // search pins to ONE concrete layer and the API rejects `all` with 400
  // (measured 2026-09-30: 10 real saves from the home page failed this way in
  // two days, the reader saw «Не удалось сохранить поиск»). Omit it and let
  // the column default (gov) apply, exactly as the plain catalog already does;
  // the 30-day rate preview below keeps `scope`, since the catalog DOES accept
  // `all` and the preview should count what the reader is looking at.
  const savedScope = scope === 'all' ? undefined : scope;
  const carriesBudget = countryConfig.features.saveSearchCarriesBudget === true;
  const { status: authStatus, user } = useAuth();
  // How many alerts this reader may keep — `Infinity` for unlimited plans,
  // otherwise the free cap the API reports in `/auth/me` (3 since 2026-10-04,
  // was 1; see `savedSearchCapFor`). Below the cap a reader who already owns
  // alerts is offered "create new" (via the picker); AT the cap only "tune
  // an existing one", with the limit line in the picker.
  const savedCap = savedSearchCapFor(user);
  const navigate = useLocaleNavigate();
  // Where the guest pressed Save — the catalogue WITH his filters in the URL
  // (`/uz?category=…`), or the closed lot under «Следить за такими». Handed
  // to /register as history state so `postAuthPath` lands him back on it.
  const authOrigin = useAuthOrigin();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [tuneOpen, setTuneOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [saved, setSaved] = useState(false);
  const [name, setName] = useState('');
  const isAuthed = authStatus === 'authed';

  // «One alert, tuned» — 97.8% of users had exactly one while the free tier
  // capped at one. Anyone who holds more, or may still add another, gets a
  // picker (below) before the builder opens.
  const { data: existing } = useQuery({
    queryKey: ['saved-searches'],
    queryFn: listSavedSearches,
    enabled: isAuthed,
  });
  const hasAlert = (existing?.length ?? 0) > 0;
  const canCreateMore = (existing?.length ?? 0) < savedCap;
  /*
   * A FOLLOW button («Следить за такими» under a closed lot) carries criteria
   * of its own — this lot's code or category. Igor, 10.10 (Redmi): it used to
   * turn into «Настроить оповещения» for anyone with an alert, and the picker
   * then led with an old, unrelated search («Стройматериалы»). Here the new
   * search for THIS lot is the first and only offer while there is room for
   * one; at the cap the picker states the limit.
   */
  const followMode = label !== undefined;
  // The alert the builder is actually editing. Deliberately NOT `existing?.[0]`
  // read reactively at render time — that query has `refetchOnWindowFocus:
  // true`, so a second alert sorting first on a background refetch could
  // silently re-target an already-open sheet mid-edit. This is set exactly
  // once per opening (at the click that opens the builder, or at the picker
  // pick), and then holds until the next explicit open — never re-derived
  // from `existing` while the sheet or picker is up.
  const [tuneTarget, setTuneTarget] = useState<SavedSearch | null>(null);
  // The free tier is capped (`savedCap`) — the API returns 403
  // saved_search_limit rather than silently replacing an existing search.
  const [limitReached, setLimitReached] = useState(false);
  // Defense-in-depth: the API's `empty_criteria` 400 (a search with no
  // subject and no other narrowing axis). `disabled={!hasFilter}` on the
  // catalog page already keeps this button from opening with nothing
  // selected, so this should be unreachable through normal use here — but it
  // is the same repository guard the Telegram bot's NL-parsed searches hit
  // routinely, and any caller of the shared endpoint could trip it too.
  const [emptyCriteria, setEmptyCriteria] = useState(false);
  // Two optional refinements, persisted with the search so ALERTS honour them.
  // The budget floor is a plain <input> — the DS ban is on OS-chrome controls
  // (select / checkbox), not text/number fields.
  const [minBudget, setMinBudget] = useState('');
  // A LIST, like the builder's — a one-word popover field meant the same
  // control held one term here and up to ten there, and a user who typed two
  // words got them matched as a single phrase instead of two exclusions.
  const [excludeTerms, setExcludeTerms] = useState<string[]>([]);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // The open panel (create form or alert picker) — scrolled into view on open.
  const panelRef = useRef<HTMLDivElement>(null);
  // Has the user typed in the name field? The suggestion is derived from the
  // region/category taxonomies, which are async — clicking Save before they
  // resolve used to freeze the raw slug in as the name, permanently. Re-deriving
  // while the field is untouched fixes that without ever overwriting typing.
  const nameEdited = useRef(false);

  const { data: regions } = useQuery({
    queryKey: ['catalog', 'regions', locale],
    queryFn: () => fetchRegions(locale),
  });
  const { data: categories } = useQuery({
    queryKey: ['catalog', 'categories', locale],
    queryFn: () => fetchCategories(locale),
  });

  /**
   * How many lots these filters matched over the last 30 days — the RATE this
   * subscription will deliver at, as distinct from `matchCount`, which is how
   * many are open right now.
   *
   * The two guard opposite failures. `matchCount === 0` warns that a search is
   * too NARROW to ever fire (26% of saved searches never have). This warns that
   * one is too BROAD: 396 of 491 saved searches are a category picked during
   * onboarding, and the biggest of them generates ~2,800 matches a week, which
   * no digest cap can make readable. Nobody picks that knowingly.
   *
   * No new endpoint: the catalog list already returns `total` and already
   * accepts `since`, so this is one existing call with limit=1. `enabled: open`
   * keeps it off the page for every visitor who never opens the popover.
   */
  const { data: rate } = useQuery({
    queryKey: ['saved-search-rate', { q, region, category, ikpu, nicheSlug, scope }],
    enabled: open,
    staleTime: 5 * 60_000,
    queryFn: () =>
      fetchCatalog({
        q,
        region,
        category,
        ikpu,
        niche: nicheSlug,
        scope,
        since: new Date(Date.now() - 30 * 86_400_000).toISOString(),
        limit: 1,
        lang: locale,
      }),
  });
  // 30 days ≈ 4.3 weeks. Plain arithmetic, not Intl — uz has no CLDR data in
  // Chrome (see the project's chrome-has-no-uz-cldr-data note), so anything
  // locale-formatted here renders correctly in Node tests and wrongly in a
  // real browser.
  const perWeek = rate ? Math.round(rate.total / 4.3) : null;

  function suggestedName(): string {
    const parts: string[] = [];
    // `category` and `nicheSlug` can each be a COMMA UNION — a saved-search
    // deep link applies every slug it carries. Looking the whole string up as
    // one slug never matches, so the fallback used to leak the raw union into
    // the name input («design-engineering,construction-buildings,…»). Resolve
    // per slug, same as the filter-bar trigger label.
    // Whole string first — the IT grouping's slug IS `it-telecom,telecom-equipment`
    // (see the filter-bar's note), so splitting before trying an exact match
    // resolves neither half and names the alert after the raw slugs.
    const cat = category
      ? (categories?.find((c) => c.slug === category)?.name ??
        category
          .split(',')
          .filter(Boolean)
          .map((slug) => categories?.find((c) => c.slug === slug)?.name ?? slug)
          .join(', '))
      : undefined;
    const reg = regions?.find((r) => r.slug === region)?.name ?? region;
    // Niches have no display-name lookup here (only their slugs) — same
    // fallback as the filter-bar trigger label.
    if (nicheSlug)
      parts.push(
        nicheSlug
          .split(',')
          .filter(Boolean)
          .map((slug) => slug.replace(/-/g, ' '))
          .join(', '),
      );
    else if (cat) parts.push(cat);
    if (reg) parts.push(reg);
    if (q) parts.push(`"${q}"`);
    return parts.length ? parts.join(' · ') : defaultName || t('saved.defaultName');
  }

  const mutation = useMutation({
    mutationFn: () => {
      // Shared id so the browser Pixel and server CAPI ActivationSavedSearch
      // event dedup into one conversion.
      const metaEventId = uuid();
      trackSavedSearchActivation(metaEventId);
      return createSavedSearch({
        name: name.trim() || t('saved.defaultName'),
        regionCode: region,
        categorySlug: category,
        ikpuCode: ikpu,
        nicheSlug,
        keyword: q,
        excludeTerms: excludeTerms.length ? excludeTerms : undefined,
        minBudgetUzs: minBudget ? Number(minBudget) : undefined,
        // Chile: the catalogue's ceiling travels with the search too — the
        // popover has a field for the floor only (`saveSearchCarriesBudget`).
        ...(carriesBudget && catalogMaxBudget ? { maxBudgetUzs: catalogMaxBudget } : {}),
        scope: savedScope,
        // A search born on `/supply` MUST opt in — `scopesMatchingSavedSearch`
        // excludes `supply` from every base case, including a search whose own
        // scope IS supply, so an unset flag here would silently match nothing.
        // `undefined` (not `false`) for every other scope: omitted is a no-op
        // server-side, exactly like `scope` above.
        includeSupply: scope === 'supply' ? true : undefined,
        metaEventId,
        // No `replace: true` here — unlike onboarding's niche switch, the
        // catalog Save button must hit the free-tier cap and
        // surface `saved_search_limit` instead of silently swapping the
        // user's existing saved search.
      });
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['saved-searches'] });
      setOpen(false);
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2200);
      // #22: nudge Telegram right after saving a category — the connect banner
      // listens for this to re-surface (even if dismissed) and highlight itself.
      window.dispatchEvent(new CustomEvent('tenders:saved-search'));
    },
    onError: (err) => {
      setLimitReached(err instanceof ApiError && err.code === 'saved_search_limit');
      setEmptyCriteria(err instanceof ApiError && err.code === 'empty_criteria');
    },
  });

  // Close on outside click / Escape. Covers both the create popover (`open`)
  // and the alert picker (`pickerOpen`) — they share the same root and never
  // show at once, so one handler is enough.
  useEffect(() => {
    if (!open && !pickerOpen) return;
    function onDown(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
        setPickerOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setOpen(false);
        setPickerOpen(false);
      }
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, pickerOpen]);

  /*
   * The panel opens DOWNWARD from the button. Igor, 10.10 (phone, 390×844,
   * ~691px of Chrome viewport): «Следить за такими» sits low on a closed lot,
   * so the form's bottom landed at ~727px — «Сохранить»/«Отмена» were off
   * screen until the reader scrolled. Nudge the panel into view on open.
   * `block: 'nearest'` is a no-op when it already fits (the desktop case), and
   * `scroll-mb-24` keeps it clear of the lot page's sticky bottom bar on
   * mobile. Optional call: jsdom has no `scrollIntoView`.
   */
  useEffect(() => {
    if (!open && !pickerOpen) return;
    panelRef.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
  }, [open, pickerOpen]);

  // Re-suggest while the popover is open and the field is untouched: whichever
  // of the two taxonomy queries resolves last still gets to name the search.
  // `setName` with an unchanged string is a no-op, so this settles immediately.
  useEffect(() => {
    if (!open || nameEdited.current) return;
    setName(suggestedName());
    // `suggestedName` is intentionally not a dep: it is re-created every
    // render, and its inputs are exactly the values listed here.
  }, [open, categories, regions, category, region, nicheSlug, q]);

  function openPopover() {
    nameEdited.current = false;
    setName(suggestedName());
    // Chile (phone QA 26.10): the floor starts from the catalogue's own
    // «Presupuesto desde», so a search saved from `?minBudget=50000000` alerts
    // on ≥ 50 M instead of storing null. Still editable; elsewhere blank.
    if (carriesBudget) setMinBudget(catalogMinBudget ? String(catalogMinBudget) : '');
    mutation.reset();
    setLimitReached(false);
    setOpen(true);
    requestAnimationFrame(() => inputRef.current?.select());
  }

  // Guests can't save yet — stash the intended search and send them to register.
  // PendingSavedSearchSync creates it for real once they're authenticated.
  function handleClick() {
    if (emptyOpensOnboarding && !(q || region || category || ikpu || nicheSlug)) {
      requestOnboardingSearch();
      if (authStatus !== 'authed') navigate('/register', { state: authOrigin });
      return;
    }
    if (authStatus !== 'authed') {
      stashPendingSavedSearch({
        name: suggestedName(),
        keyword: q,
        regionCode: region,
        categorySlug: category,
        ikpuCode: ikpu,
        nicheSlug,
        scope: savedScope,
        // Same "supply must opt in explicitly" rule as the authed path above —
        // a guest saving from /supply must not lose that on the replay.
        includeSupply: scope === 'supply' ? true : undefined,
        // Spain: the budget band he filtered by travels with the search.
        ...(countryConfig.features.guestSearchCarried === true
          ? {
              ...(catalogMinBudget ? { minBudgetUzs: catalogMinBudget } : {}),
              ...(catalogMaxBudget ? { maxBudgetUzs: catalogMaxBudget } : {}),
            }
          : {}),
      });
      /*
       * Back to the list he chose, not to «Toifani tanlang» (Igor's phone run,
       * 17.10, Redmi uz): a guest picked «Bino qurilishi» — 249 lots — pressed
       * «Qidiruvni saqlash», registered, went through ИНН + Telegram and
       * landed on the unfiltered home with all 3 517 lots. The search WAS
       * saved; the screen just forgot it. `/register` used to get no `from`,
       * so `postAuthPath` fell through to `/`.
       *
       * The onboarding sheet's own landing (8edb724d, «Ваша подборка») only
       * fires for a search saved INSIDE the sheet; this one is saved by
       * `PendingSavedSearchSync`, so the sheet now simply closes onto the page
       * the origin brought him back to — the same filters, from the URL.
       */
      navigate('/register', { state: authOrigin });
      return;
    }
    if (followMode && !canCreateMore) {
      setPickerOpen(true);
      return;
    }
    openPopover();
  }

  // «Настроить оповещения». With exactly one alert and no room for another
  // this is the DEFAULT path — straight to the builder, no picker, no extra
  // click. A genuine choice (several alerts, or room to create one more)
  // summons the picker.
  function openTune() {
    if (!existing || existing.length === 0) return;
    // A single existing alert goes straight to the builder ONLY when the reader
    // is AT their cap and can never own a second one. Anyone below the cap —
    // a paid (unlimited) reader, and since 2026-10-01 a free reader with fewer
    // than 3 — must still be offered "create new", so route them through the
    // picker, which lists the existing alert(s) to tune AND a "create new"
    // action, rather than silently editing their one search.
    if (existing.length === 1 && !canCreateMore) {
      setTuneTarget(existing[0]);
      setTuneOpen(true);
      return;
    }
    setPickerOpen(true);
  }

  function handlePick(s: SavedSearch) {
    setTuneTarget(s);
    setPickerOpen(false);
    setTuneOpen(true);
  }

  function handleCreateNewFromPicker() {
    setPickerOpen(false);
    openPopover();
  }

  return (
    <div ref={rootRef} className="relative block w-full md:inline-block md:w-auto">
      {saved ? (
        // Checked BEFORE `hasAlert`: on success, invalidating ['saved-searches']
        // refetches and can populate `existing` well within this span's 2.2s
        // display window (invalidateQueries awaits the refetch), which would
        // otherwise flip straight to the "tune" button and the confirmation
        // would never be seen at all. Checking `saved` first guarantees it
        // wins for its full window regardless of how fast the refetch lands.
        <span className="inline-flex h-11 w-full items-center justify-center gap-1.5 rounded-pill border border-brand/30 bg-brand-dim px-3 text-[12.5px] font-semibold text-brand md:h-8 md:w-auto md:justify-start">
          <CheckIcon className="h-3 w-3" />
          {t('saved.saved')}
        </span>
      ) : hasAlert && !followMode ? (
        // An alert already exists — the catalog CTA offers to TUNE it (or,
        // below the cap, to create another from the picker) rather than a
        // bare second "Save". `openTune` decides whether a picker is needed.
        <button
          type="button"
          onClick={openTune}
          className="inline-flex h-11 w-full items-center justify-center gap-1.5 rounded-pill border border-brand/30 bg-bone-surface px-3 text-[12.5px] font-semibold text-brand transition-colors hover:bg-brand-dim md:h-8 md:w-auto md:justify-start"
        >
          <BookmarkIcon className="h-3.5 w-3.5" />
          {t('saved.tuneAlert')}
        </button>
      ) : (
        <button
          type="button"
          onClick={handleClick}
          disabled={disabled}
          title={authStatus !== 'authed' ? t('catalog.saveSearchGuest') : undefined}
          className={
            disabled
              ? 'inline-flex h-11 w-full cursor-not-allowed items-center justify-center gap-1.5 rounded-pill border border-edge bg-bone px-3 text-[12.5px] font-semibold text-ink-muted md:h-8 md:w-auto md:justify-start'
              : highlight
                ? 'inline-flex h-11 w-full items-center justify-center gap-1.5 rounded-pill bg-orange-ink px-3.5 text-[12.5px] font-bold text-white shadow-m-sm ring-[3px] ring-orange/30 transition-[filter] hover:brightness-95 md:h-8 md:w-auto md:justify-start'
                : 'inline-flex h-11 w-full items-center justify-center gap-1.5 rounded-pill border border-brand/30 bg-bone-surface px-3 text-[12.5px] font-semibold text-brand transition-colors hover:bg-brand-dim md:h-8 md:w-auto md:justify-start'
          }
        >
          <BookmarkIcon className="h-3.5 w-3.5" />
          {label ?? t('catalog.saveSearch')}
        </button>
      )}

      {open && (
        <div
          ref={panelRef}
          data-testid="save-search-panel"
          className="absolute left-0 z-40 mt-1.5 w-[280px] max-w-[calc(100vw-2.5rem)] scroll-mb-24 rounded-lg border border-edge bg-bone-surface p-3 shadow-m-md sm:left-auto sm:right-0 lg:scroll-mb-4"
        >
          {/* The two volume lines describe the SEARCH, not the name field, so
              they lead the panel. They used to sit between the name label and
              its input, which read as if 943 were a hint for the name. */}
          {matchCount != null && (
            <p
              className={`mb-2 text-[12px] ${matchCount === 0 ? 'font-semibold text-red-600' : 'text-ink-muted'}`}
            >
              {matchCount === 0 ? t('saved.matchesNone') : t('saved.matches', { n: matchCount })}
            </p>
          )}
          {perWeek != null && perWeek > 0 && (
            <p
              className={`mb-2 text-[12px] ${perWeek >= 100 ? 'text-orange-ink' : 'text-ink-muted'}`}
            >
              {t('saved.rate', { n: perWeek })}
            </p>
          )}
          {/* Same label treatment as «Бюджет от» / «Исключить» below — the
              caps-and-tracking variant read as a section heading rather than
              a field label, which is what the other two are. */}
          <label
            htmlFor="save-search-name"
            className="mb-1 block text-[11px] font-medium text-ink-muted"
          >
            {t('saved.nameLabel')}
          </label>
          <input
            id="save-search-name"
            ref={inputRef}
            type="text"
            value={name}
            onChange={(e) => {
              nameEdited.current = true;
              setName(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !mutation.isPending) mutation.mutate();
            }}
            className="mb-2 h-10 w-full rounded-md border border-edge bg-bone px-2.5 text-[16px] text-ink outline-none focus:border-brand"
          />

          {/* Two optional refinements. The catalog could always filter on a
              budget floor, but saving dropped it, so alerts ignored a bound the
              user had set. The exclusions are the one axis the catalog has no
              equivalent for — which is also why the digest "+N ещё" link cannot
              reproduce it (see ADR 0007). */}
          <label
            htmlFor="save-search-min-budget"
            className="mb-1 block text-[11px] font-medium text-ink-muted"
          >
            {t('saved.minBudgetLabel')}
          </label>
          {/* Grouped as typed, «= 100 mln so'm» under it, quick picks — the
              same field as the alert editor (Igor's Redmi, 17.10). */}
          <BudgetInput
            id="save-search-min-budget"
            value={minBudget}
            onChange={setMinBudget}
            presets={BUDGET_FLOOR_PICKS}
            placeholder={t('saved.minBudgetPlaceholder')}
            wrapperClassName="mb-2 flex min-w-0 flex-col gap-1"
            className="h-10 w-full rounded-md border border-edge bg-bone px-2.5 text-[16px] text-ink outline-none placeholder:text-ink-muted focus:border-brand"
          />
          <label className="mb-1 block text-[11px] font-medium text-ink-muted">
            {t('saved.excludeTermsLabel')}
          </label>
          <div className="mb-2">
            <TagInput
              value={excludeTerms}
              onChange={setExcludeTerms}
              ariaLabel={t('saved.excludeTermsLabel')}
              placeholder={t('saved.excludeTermsPlaceholder')}
              max={SAVED_SEARCH_TERMS_MAX}
            />
          </div>

          {mutation.isError &&
            (limitReached ? (
              <p className="mb-2 text-[12px] text-red-600">
                {t('saved.limitReached', { n: Number.isFinite(savedCap) ? savedCap : 1 })}{' '}
                <LocaleLink to="/plans" className="font-semibold underline">
                  {t('tender.cta.pro')}
                </LocaleLink>
              </p>
            ) : emptyCriteria ? (
              <p className="mb-2 text-[12px] text-red-600">{t('saved.emptyCriteria')}</p>
            ) : (
              <p className="mb-2 text-[12px] text-red-600">
                {/* Never the raw message: a network failure printed «Failed to
                    fetch» and a server rejection its English text, in both
                    locales (tester, 2026-09-22). The two known refusals are
                    handled above; everything else is one translated line. */}
                {t('saved.errorGeneric')}
              </p>
            ))}
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="inline-flex h-9 items-center rounded-md px-3 text-[12.5px] font-medium text-ink-soft transition-colors hover:bg-bone"
            >
              {t('saved.cancel')}
            </button>
            <button
              type="button"
              onClick={() => mutation.mutate()}
              disabled={mutation.isPending}
              className="inline-flex h-9 items-center rounded-md bg-brand px-3.5 text-[12.5px] font-semibold text-white transition-colors hover:bg-brand/90 disabled:opacity-60"
            >
              {t('saved.save')}
            </button>
          </div>
        </div>
      )}

      {pickerOpen && (
        // Shown when there is a choice to make — see `openTune`. AT the cap
        // the picker drops "create new" and states the limit instead (with the
        // Pro link), so the reader learns why rather than meeting a 403.
        <div
          ref={panelRef}
          className="absolute left-0 z-40 mt-1.5 w-[280px] max-w-[calc(100vw-2.5rem)] scroll-mb-24 rounded-lg border border-edge bg-bone-surface p-3 shadow-m-md sm:left-auto sm:right-0 lg:scroll-mb-4"
        >
          <AlertPicker
            searches={existing ?? []}
            onPick={handlePick}
            onCreateNew={canCreateMore ? handleCreateNewFromPicker : undefined}
          >
            {!canCreateMore && Number.isFinite(savedCap) && (
              <p className="text-[12px] text-ink-muted">
                {t('saved.limitReached', { n: savedCap })}{' '}
                <LocaleLink to="/plans" className="font-semibold text-brand underline">
                  {t('tender.cta.pro')}
                </LocaleLink>
              </p>
            )}
          </AlertPicker>
        </div>
      )}

      {/* A short confirmation where the eye is (Igor, 10.10): the inline
          «Сохранено» pill replaces a button that may sit under the lot page's
          sticky bar or off-screen once the popover closes. Above that bar
          (z-30), below dialogs (z-50). */}
      {saved && (
        <div
          role="status"
          className="pointer-events-none fixed inset-x-0 bottom-24 z-40 flex justify-center px-4 lg:bottom-8"
        >
          <span className="inline-flex items-center gap-1.5 rounded-pill bg-ink px-4 py-2.5 text-[13.5px] font-semibold text-white shadow-m-md">
            <CheckIcon className="h-3.5 w-3.5" />
            {t('saved.toast')}
          </span>
        </div>
      )}

      <SavedSearchEditSheet search={tuneTarget} open={tuneOpen} onOpenChange={setTuneOpen} />
    </div>
  );
}
